import { useCallback, useEffect, useState } from 'react';
import { crm, type ProjectRow } from '@/lib/crm-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

interface Props {
  /**
   * Fehlt bei einem internen Projekt — der eigenen Website, einer
   * Marketing-Aktion. Die brauchen dieselbe Führung und haben keinen
   * Kunden, dem man sie unterschieben könnte.
   */
  customerId?: string;
  project?: ProjectRow;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Anlegen und Bearbeiten eines Projekts in einem Dialog — wie `OrderDialog`.
 */
export function ProjectDialog({ customerId, project, onClose, onSaved }: Props) {
  const [titel, setTitel] = useState(project?.title ?? '');
  const [stand, setStand] = useState(project?.summary ?? '');
  const [start, setStart] = useState(project?.startDate ?? '');
  const [faellig, setFaellig] = useState(project?.dueDate ?? '');
  // Als Euro-Text im Feld, als Cent in der Datenbank. Leer bleibt leer —
  // „nicht gesetzt“ ist eine andere Aussage als „null Euro“.
  const [budget, setBudget] = useState(
    project?.budgetCents != null ? String(project.budgetCents / 100) : '',
  );
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  const speichern = useCallback(async () => {
    const name = titel.trim();
    if (!name) {
      setFehler('Das Projekt braucht einen Titel.');
      return;
    }
    const budgetText = budget.trim().replace(',', '.');
    let budgetCents: number | null = null;
    if (budgetText) {
      const euro = Number.parseFloat(budgetText);
      if (!Number.isFinite(euro) || euro < 0) {
        setFehler('Das Budget muss eine Zahl sein, oder leer bleiben.');
        return;
      }
      budgetCents = Math.round(euro * 100);
    }

    setBusy(true);
    setFehler(null);
    try {
      const felder = {
        title: name,
        summary: stand.trim() || null,
        startDate: start || null,
        dueDate: faellig || null,
        budgetCents,
      };
      if (project) await crm.projects.update(project.id, felder);
      else await crm.projects.create({ customerId: customerId ?? null, ...felder });
      onSaved();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
      setBusy(false);
    }
  }, [budget, customerId, faellig, onSaved, project, stand, start, titel]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-6">
      <div className="w-full max-w-lg space-y-4 rounded-lg bg-card p-6 shadow-xl">
        <h2 className="text-lg font-semibold">
          {project
            ? 'Projekt bearbeiten'
            : customerId
              ? 'Neues Projekt'
              : 'Neues internes Projekt'}
        </h2>
        {!project && !customerId && (
          <p className="text-sm text-muted-foreground">
            Ohne Kunden — für die eigene Website, epasuBuch, Marketing.
          </p>
        )}

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Titel</span>
          <Input
            value={titel}
            onChange={(e) => setTitel(e.target.value)}
            placeholder="z. B. Abschlussfotos 2027"
            autoFocus
          />
        </label>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Stand</span>
          <Input
            value={stand}
            onChange={(e) => setStand(e.target.value)}
            placeholder="Der Satz, der es erklärt"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Start</span>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="font-medium">Fällig</span>
            <Input
              type="date"
              value={faellig}
              onChange={(e) => setFaellig(e.target.value)}
            />
          </label>
        </div>

        <label className="block space-y-1 text-sm">
          <span className="font-medium">Budget</span>
          <Input
            inputMode="decimal"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="in Euro, leer lassen heißt „nicht gesetzt“"
          />
        </label>

        {fehler && <p className="text-sm text-danger">{fehler}</p>}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button onClick={() => void speichern()} disabled={busy}>
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
